import React from 'react';
import styled from 'styled-components';
import SectionCard from './SectionCard';
import FieldDisplay from './FieldDisplay';
import { Text, TextBlock } from 'bench-internal-theme';
import { colors } from 'bench-style-constants';
import { FiscalYearEnd, TaxPreparerInfo } from '../types';

const FieldsGrid = styled.div`
  display: grid;
  grid-template-columns: repeat(2, 1fr);
  gap: 16px;
`;

const StatusBanner = styled.div<{ isActive: boolean }>`
  padding: 12px 16px;
  border-radius: 4px;
  background-color: ${({ isActive }) => (isActive ? colors.shoot : colors.brook)};
  border: 1px solid ${({ isActive }) => (isActive ? colors.stem : colors.water)};
`;

const StatusText = styled(Text).attrs({
  styleType: 'medium',
})<{ isActive: boolean }>`
  color: ${({ isActive }) => (isActive ? colors.stem : colors.water)};
`;

const TaxPreparerCard = styled.div`
  padding: 16px;
  border: 1px solid ${colors.silt};
  border-radius: 4px;
  display: grid;
  gap: 12px;
`;

const CardTitle = styled(TextBlock).attrs({
  styleType: 'mediumBold',
  color: colors.charcoal,
})``;

interface TaxDetailsSectionProps {
  taxClientStatus?: string;
  filingType?: string;
  fiscalYearEnd?: FiscalYearEnd;
  taxPreparer?: TaxPreparerInfo;
  filesDelawareFranchiseTax?: boolean;
  track1099?: boolean;
}

const MONTH_NAMES = [
  'January',
  'February',
  'March',
  'April',
  'May',
  'June',
  'July',
  'August',
  'September',
  'October',
  'November',
  'December',
];

const formatFiscalYearEnd = (fiscalYearEnd?: FiscalYearEnd): string => {
  if (!fiscalYearEnd?.month) return '-';
  const monthName = MONTH_NAMES[fiscalYearEnd.month - 1] || '-';
  if (fiscalYearEnd.day) {
    return `${monthName} ${fiscalYearEnd.day}`;
  }
  return monthName;
};

const formatBoolean = (value?: boolean): string => {
  if (value === undefined || value === null) return '-';
  return value ? 'Yes' : 'No';
};

const showDelawareFranchiseTax = (filingType?: string): boolean => {
  return ['C-Corp', 'S-Corp', 'LLC'].includes(filingType || '');
};

const TaxDetailsSection: React.FC<TaxDetailsSectionProps> = ({
  taxClientStatus,
  filingType,
  fiscalYearEnd,
  taxPreparer,
  filesDelawareFranchiseTax,
  track1099,
}) => {
  const isActiveTaxClient = taxClientStatus === 'Active';
  const showTaxPreparer = !isActiveTaxClient && taxPreparer;

  return (
    <SectionCard title="Tax Details">
      <StatusBanner isActive={isActiveTaxClient}>
        <StatusText isActive={isActiveTaxClient}>
          {isActiveTaxClient
            ? 'This client is enrolled in Bench Tax.'
            : 'This client is not enrolled in Bench Tax.'}
        </StatusText>
      </StatusBanner>

      <FieldsGrid>
        <FieldDisplay label="Tax Filing Type" value={filingType} />
        <FieldDisplay label="Fiscal Year End" value={formatFiscalYearEnd(fiscalYearEnd)} />
      </FieldsGrid>

      {showTaxPreparer && (
        <TaxPreparerCard>
          <CardTitle>Tax Preparer</CardTitle>
          <FieldsGrid>
            <FieldDisplay label="Full Name" value={taxPreparer.fullName} />
            <FieldDisplay label="Email" value={taxPreparer.email} />
            <FieldDisplay
              label="Has Access to Bench"
              value={formatBoolean(taxPreparer.hasAccessToBench)}
            />
          </FieldsGrid>
        </TaxPreparerCard>
      )}

      <FieldsGrid>
        {showDelawareFranchiseTax(filingType) && (
          <FieldDisplay
            label="Files Delaware Franchise Tax"
            value={formatBoolean(filesDelawareFranchiseTax)}
          />
        )}
        <FieldDisplay label="Track 1099" value={formatBoolean(track1099)} />
      </FieldsGrid>
    </SectionCard>
  );
};

export default TaxDetailsSection;
